import type { TransferDetailState } from "@/types";
import { transferStatusState } from "@/features/transfers/transfer-state";

/**
 * The stages a governed transfer moves through (ALL-5794), built only from the gateway's
 * `TransferView.status` and `timeline[{state, at}]`. A stage is "done" when the transfer has moved
 * past it, "current" while it is there, "failed" where it stopped, and "upcoming" otherwise. A
 * timestamp is shown only when the timeline recorded one; nothing is estimated.
 */

export type LifecycleStageId = "intent" | "policy" | "approval" | "signing" | "broadcast" | "confirmed";
export type LifecycleStageStatus = "done" | "current" | "failed" | "unknown" | "upcoming";

export interface LifecycleStage {
  id: LifecycleStageId;
  label: string;
  status: LifecycleStageStatus;
  /** ISO instant the transfer entered this stage, when the timeline recorded it. */
  at?: string;
}

const STAGES: { id: LifecycleStageId; label: string }[] = [
  { id: "intent", label: "Intent created" },
  { id: "policy", label: "Policy check" },
  { id: "approval", label: "Approval" },
  { id: "signing", label: "Signing" },
  { id: "broadcast", label: "Broadcast" },
  { id: "confirmed", label: "Confirmed" },
];

/** Which stage a public state belongs to; terminal failure states return null. */
export function stageOfState(state: TransferDetailState): LifecycleStageId | null {
  switch (state) {
    case "draft":
      return "intent";
    case "pending-policy":
      return "policy";
    case "pending-approval":
      return "approval";
    case "approved":
    case "pending-device-signature":
      return "signing";
    case "broadcast":
    case "outcome-unknown":
      return "broadcast";
    case "confirmed":
    case "broadcast-confirmed":
    case "reconciled":
      return "confirmed";
    default:
      return null; // rejected, failed
  }
}

function index(id: LifecycleStageId): number {
  return STAGES.findIndex((s) => s.id === id);
}

export function buildLifecycle(
  status: string,
  timeline: { state: string; at: string }[],
  createdAt?: string,
): LifecycleStage[] {
  const state = transferStatusState(status);

  // The first instant the timeline shows the transfer in each stage.
  const enteredAt = new Map<LifecycleStageId, string>();
  let furthest = -1;
  for (const entry of timeline) {
    const stage = stageOfState(transferStatusState(entry.state));
    if (!stage) continue;
    if (!enteredAt.has(stage) && entry.at) enteredAt.set(stage, entry.at);
    furthest = Math.max(furthest, index(stage));
  }
  if (createdAt && !enteredAt.has("intent")) enteredAt.set("intent", createdAt);

  const here = stageOfState(state);
  let currentIndex: number;
  let terminal: LifecycleStageStatus | null = null;
  if (here) {
    currentIndex = index(here);
    if (state === "outcome-unknown") terminal = "unknown";
  } else {
    // Rejected stops at approval when the transfer got that far, otherwise at the policy check
    // (a policy rejection); failed stops at the furthest stage the timeline reached.
    if (state === "rejected") currentIndex = furthest >= index("approval") ? index("approval") : index("policy");
    else currentIndex = Math.max(furthest, index("policy"));
    terminal = "failed";
  }
  const completedAll = state === "confirmed" || state === "reconciled" || state === "broadcast-confirmed";

  return STAGES.map((s, i) => {
    let status: LifecycleStageStatus;
    if (completedAll) status = "done";
    else if (i < currentIndex) status = "done";
    else if (i === currentIndex) status = terminal ?? "current";
    else status = "upcoming";
    return { id: s.id, label: s.label, status, at: enteredAt.get(s.id) };
  });
}
